import type { WorkflowTemplate } from '../../contracts/routines.types';

/**
 * Presets that exist in API responses but must not ship in Explore,
 * Marketplace, recommendation strips, or Connected-apps Magic.
 *
 * `inbox-mail-triage` is descoped rather than flag-gated: the companion
 * backend PR unpublishes it from the connector catalog and stops auto-install
 * so every client matches. This list is the FE hide for older catalog
 * responses, same pattern as the other unreleased ids.
 */
export const UNRELEASED_PRESET_IDS = [
  'ehr-sync-sweep',
  'coding-billing-review',
  'inbox-mail-triage',
  'template-tuner',
  'patient-summary-comms',
  'daily-session-insights',
] as const;

/** Always surfaced first in recommendation strips when not yet installed. */
export const SPOTLIGHT_PRESET_IDS = ['prep-my-day'] as const;

export function isReleasedPreset(templateId: string): boolean {
  return !(UNRELEASED_PRESET_IDS as readonly string[]).includes(templateId);
}

const SPOTLIGHT_LIMIT = 3;

function mulberry32(seed: number): () => number {
  let t = seed;
  return () => {
    t = (t + 0x6d2b79f5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Pick up to three uninstalled presets for the recommendation strip.
 * Spotlight presets stay pinned in order; remaining slots are filled from a
 * seeded shuffle of the rest.
 */
export function pickRecommendedPresets(
  pool: readonly WorkflowTemplate[],
  shuffleSeed: number,
  limit = SPOTLIGHT_LIMIT
): WorkflowTemplate[] {
  const released = pool.filter((preset) => isReleasedPreset(preset.template_id));
  const byId = new Map(released.map((preset) => [preset.template_id, preset]));
  const picked: WorkflowTemplate[] = [];

  for (const templateId of SPOTLIGHT_PRESET_IDS) {
    const preset = byId.get(templateId);
    if (preset) picked.push(preset);
  }

  const pickedIds = new Set(picked.map((preset) => preset.template_id));
  const remainder = released.filter((preset) => !pickedIds.has(preset.template_id));
  const rand = mulberry32(shuffleSeed);
  const shuffled = [...remainder].sort(() => rand() - 0.5);

  return [...picked, ...shuffled].slice(0, limit);
}
